import type { GalleryContentQuery } from 'shared/types/gallery/content-query'

/**
 * Builds a stable identity key for a color swatch, derived from its visual color
 * composition rather than `designId`.
 *
 * `designId` is not safe to persist across content re-fetches for personalized
 * ("custom color variation") swatches: any personalization axis change (text, image,
 * or color) triggers a full content re-fetch, and Design Conductor is not guaranteed
 * to return the same `designId` for the same visual color on a subsequent request.
 * `colorComposition` describes what's actually rendered (see
 * `TemplateTileColorSwatchWrapper`/`getColorSwatchColors`), so it survives a re-fetch
 * even when `designId` doesn't.
 *
 * Colors are lowercased and sorted before joining so the key is insensitive to hex
 * casing and to any reordering of `colorComposition` entries between requests. DC's
 * swatch-order instability (DIYDR-1995) is only documented at the swatch-list level,
 * but nothing guarantees composition-internal order is stable either, so the key
 * doesn't rely on it.
 */
export const getColorSwatchIdentityKey = (swatch: GalleryContentQuery.ColorSwatch): string => {
  if (swatch.colorComposition?.length) {
    return swatch.colorComposition
      .map(({ color }) => color.toLowerCase())
      .sort()
      .join('|')
  }

  return swatch.color?.toLowerCase() ?? swatch.designId
}
